import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  ColumnHeader,
  DocSignService,
  EgibObject,
  FormAlertService,
  MapId,
  MapObject,
  MapObjectApiType,
  MapObjectTableState,
  MapPortalName,
  MapSettings,
  MapSettingsService,
  MapState,
  MapStateService,
  PortalId,
  PzService,
  StreetService,
  ToolType,
  disableValidators,
  markFormGroupsTouched,
  restoreValidators,
} from '@gk/gk-modules';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  from,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { MultiPersonDataComponent } from '../../../components/multi-person-data/multi-person-data.component';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { RequestInProgress } from '../../../guards/request-in-progress/request-in-progress.guard';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { PlaceService } from '../../../services/place/place.service';
import {
  BsMessageType,
  WorkspaceStateDraftVersion,
  WorkspaceStateId,
} from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { DateRangePickerComponent } from '../../../shared/date-range-picker/date-range-picker.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import {
  DateHelperUtil,
  NgbDateRange,
} from '../../../utils/date-helper/date-helper.util';
import { FellingObjectsControlName } from '../../environmental-protection-portal/trees-and-shrubs-felling-request/services/new-request-form/new-request-form.model';
import {
  MIRNewRequestControlName,
  MIRNewRequestFormGroup,
} from '../services/new-request-form/new-request-form.model';
import { MIRNewRequestFormService } from '../services/new-request-form/new-request-form.service';
import {
  MIRNewRequest,
  MIRNewRequestDto,
  MIRNewRequestFormWorkspaceState,
  MIRRequestMode,
} from '../services/new-request/new-request.model';
import { MainRoutes } from './../../../guards/guard.models';

@Component({
  selector: 'app-mir-new-request',
  templateUrl: './new-request.component.html',
  standalone: false,
})
export class MIRNewRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy, RequestInProgress
{
  override controlName = MIRNewRequestControlName;
  override formGroup: FormGroup<MIRNewRequestFormGroup>;
  override portalId = PortalId.MonumentInstallationRequest;
  readonly mapObjectTableState = new MapObjectTableState(
    [
      new ColumnHeader('districtName', 'PARCEL_SEARCH_FORM.DISTRICT'),
      new ColumnHeader('mapObjectNumber', 'GK.MAP.PARCEL_OR_BUILDING_NUMBER'),
      new ColumnHeader('address', 'GK.MAP.ADDRESS'),
      new ColumnHeader('mapSheet', 'PARCEL_SEARCH_FORM.SHEET'),
      new ColumnHeader('typeName', 'GK.MAP.TYPE'),
    ],
    undefined,
    this.defaultMapGeometryStyles,
    true,
    true,
    true,
    '25',
  );
  mapSettings: MapSettings;
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild('ownerDetailsComponent')
  ownerDetailsComponent: MultiPersonDataComponent;
  @ViewChild('canDeactivateModal') canDeactivateModal: NgbModalRef;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  @ViewChild('monumentInstallationDateRangeComponent')
  monumentInstallationDateRangeComponent: DateRangePickerComponent;
  successSigned = false;
  workspaceStateDraftVersion =
    WorkspaceStateDraftVersion.MonumentInstallationRequestFormVersion;
  workspaceStateId = WorkspaceStateId.MonumentInstallationRequestForm;
  fellingObjectsControlName = FellingObjectsControlName;

  constructor(
    public override mapStateService: MapStateService,
    public override mapSettingsService: MapSettingsService,
    public override formAlertService: FormAlertService,
    public override placeService: PlaceService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    private mirNewRequestFormService: MIRNewRequestFormService,
    public override streetService: StreetService,
    public override toastrService: ToastrService,
    public override router: Router,
    private modalService: NgbModal,
    public override pzService: PzService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
      placeService,
      streetService,
      undefined,
      toastrService,
    );
  }

  override ngOnInit(): void {
    this.fetchWrongFileText();
    this.subscribeToDocSignTranslations();
    this.createForm();
    this.fetchMapSettings();
    this.handleRequestModeChange();
    this.preventDateFromPast(this.getFoodCourtStartDateFormControl());
  }

  createForm(): void {
    this.formGroup = this.mirNewRequestFormService.getFormGroup();
  }

  fetchMapSettings(): void {
    this.mapSettingsService
      .getMapSettings(MapPortalName.Applicant)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((mapSettings) => {
        this.mapSettings = mapSettings;
        this.setMapState();
      });
  }

  override setMapState(): void {
    this.mapState = new MapState(
      MapId.OwnershipConversionFeeCalculation,
      this.mapStateService.getViewState(
        MapId.OwnershipConversionFeeCalculation,
        this.mapSettings,
      ),
      this.mapStateService.getToolbarState(
        [ToolType.LandParcel, ToolType.Building],
        this.mapSettings.papers,
        undefined,
        undefined,
        true,
      ),
      this.initialToolsState,
      this.mapStateService.getLayersState(
        MapId.OwnershipConversionFeeCalculation,
        this.mapSettings,
        MapPortalName.Applicant,
      ),
      [this.mapObjectTableState],
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.isRequestDirty() && !this.successSigned
      ? from(
          this.modalService.open(this.canDeactivateModal, {
            beforeDismiss: () => false,
          }).result,
        )
      : true;
  }

  isRequestDirty(): boolean {
    return (
      this.formGroup.dirty || this.attachmentsComponent.areChosenDocuments()
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid &&
      this.attachmentsComponent.areDocumentsValid() &&
      this.isMapSectionValid() &&
      (this.isFoodCourtMode() || !this.isMonumentInstallationDateRangeInvalid())
    );
  }

  handleSubmit(): void {
    this.submitted = true;
    markFormGroupsTouched(this.getInvestorDetailsFormGroup());
    if (!this.isRequestValid()) {
      return;
    }

    this.sendRequest();
  }

  sendRequest(): void {
    this.getRequestBody()
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap((body) =>
          this.docSignService.addToSign(
            body,
            '/api/interesant/wniosek/wydanieDecyzjiMiejskiegoKonserwatoraZabytkow/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: (error) => this.handleSendAndValidateFailure(error),
      });
  }

  getRequestBody = (): Observable<MIRNewRequestDto> => {
    return from(
      Promise.all(this.attachmentsComponent.getConvertedFiles()).catch(
        (error) => new Error(error),
      ),
    ).pipe(
      switchMap((apiFiles) => {
        const apiFilesArray = Object.values(apiFiles).flatMap((file) => file);
        if (
          (Array.isArray(apiFilesArray) &&
            apiFilesArray.some((file) => file instanceof Error)) ||
          apiFiles instanceof Error
        ) {
          this.errorSubmitMessage = this.wrongFilesErrorText;
          this.toastrService.error(this.wrongFilesErrorText);

          return throwError(() => new Error(this.wrongFilesErrorText));
        }

        return of(apiFiles);
      }),
      map((apiFiles) =>
        MIRNewRequest.fromAppToApi(
          this.getFormValue(),
          this.getLandParcels(),
          this.getBuildings(),
          apiFiles as ApiNewDokumentPowiazanyDalDto[],
        ),
      ),
      catchError((err) => {
        console.error(err);

        return throwError(() => err);
      }),
    );
  };

  getWorkspaceStateToSave = (): MIRNewRequestFormWorkspaceState => ({
    formValue: this.getFormValue(),
    mapState: this.mapState,
  });

  handleLoadWorkspaceResponse(state: MIRNewRequestFormWorkspaceState): void {
    if (state.formValue?.monumentInstallationDateRange) {
      this.monumentInstallationDateRangeComponent.setPeriod(
        state.formValue.monumentInstallationDateRange,
      );
    }
    this.formGroup.patchValue({
      ...state.formValue,
      investorDetails: {
        ...state.formValue.investorDetails,
        addressSelect: undefined,
      },
    });
    if (state.formValue?.investorDetails?.addressSelect) {
      this.investorDetailsComponent.setAddressSelectFormGroupValue(
        state.formValue.investorDetails.addressSelect,
      );
    }
    if (state.formValue?.proxyDetailsArray) {
      this.proxyDetailsComponent.setFormGroupValue(
        state.formValue.proxyDetailsArray,
      );
    }
    this.requestReplyFormComponent.patchRequestReplyFormControls({
      documentationForm: state.formValue.documentationForm,
      shippingForm: state.formValue.shippingForm,
    });
    this.mapState = state.mapState;
  }

  getLandParcels(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.LandParcel)
      ),
    );
  }

  getBuildings(): EgibObject[] {
    return [...this.mapState.mapObjectTablesState[0].mapObjects].filter(
      <(mapObject: MapObject) => mapObject is EgibObject>(
        ((mapObject) => mapObject.type === MapObjectApiType.Building)
      ),
    );
  }

  getFormValue(): MIRNewRequest {
    return this.formGroup.getRawValue();
  }

  override handleDocSignSuccess(): void {
    this.successSigned = true;
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.MonumentInstallationRequest}/requests-list`,
    );
  }

  override onRequestModeChange(requestMode: MIRRequestMode): void {
    this.getObjectDescriptionFormControl().reset();
    const foodCourtFormControls = this.getFoodCourtFormControls();
    const monumentInstallationFormControls =
      this.getMonumentInstallationFormControls();
    switch (requestMode) {
      case MIRRequestMode.FoodCourt: {
        restoreValidators(foodCourtFormControls);
        disableValidators(monumentInstallationFormControls);

        break;
      }
      case MIRRequestMode.MonumentInstallation: {
        restoreValidators(monumentInstallationFormControls);
        disableValidators(foodCourtFormControls);

        break;
      }
      default: {
        console.warn(`Unknown request mode: ${requestMode}`);
      }
    }
  }

  getFoodCourtFormControls(): AbstractControl[] {
    return [this.getFoodCourtStartDateFormControl()];
  }

  getMonumentInstallationFormControls(): AbstractControl[] {
    return [
      this.getWorkScopeFormControl(),
      this.getProjectFormControl(),
      this.getMonumentInstallationDateRangeFormControl(),
    ];
  }

  isFoodCourtMode(): boolean {
    return this.getRequestModeValue() === MIRRequestMode.FoodCourt;
  }

  isMonumentInstallationMode(): boolean {
    return this.getRequestModeValue() === MIRRequestMode.MonumentInstallation;
  }

  isMapSectionValid(): boolean {
    return this.mapState.mapObjectTablesState[0].mapObjects.length > 0;
  }

  isObjectDescriptionFormControlValid(): boolean {
    return this.getObjectDescriptionFormControl().valid;
  }

  getObjectDescriptionFormControl(): UntypedFormControl {
    return this.formGroup.get(
      MIRNewRequestControlName.ObjectDescription,
    ) as UntypedFormControl;
  }

  isFoodCourtStartDateFormControlValid(): boolean {
    return this.getFoodCourtStartDateFormControl().valid;
  }

  getFoodCourtStartDateFormControl(): UntypedFormControl {
    return this.formGroup.get(
      MIRNewRequestControlName.FoodCourtStartDate,
    ) as UntypedFormControl;
  }

  isJustificationFormControlValid(): boolean {
    return this.getJustificationFormControl().valid;
  }

  getJustificationFormControl(): UntypedFormControl {
    return this.formGroup.get(
      MIRNewRequestControlName.Justification,
    ) as UntypedFormControl;
  }

  isWorkScopeFormControlValid(): boolean {
    return this.getWorkScopeFormControl().valid;
  }

  getWorkScopeFormControl(): UntypedFormControl {
    return this.formGroup.get(
      MIRNewRequestControlName.WorkScope,
    ) as UntypedFormControl;
  }

  isProjectFormControlValid(): boolean {
    return this.getProjectFormControl().valid;
  }

  getProjectFormControl(): UntypedFormControl {
    return this.formGroup.get(
      MIRNewRequestControlName.Project,
    ) as UntypedFormControl;
  }

  isMonumentInstallationDateRangeInvalid(): boolean {
    return (
      !this.isMonumentInstallationDateRangeCorrect() ||
      this.isMonumentInstallationDateRangeEmpty()
    );
  }

  isMonumentInstallationDateRangeCorrect(): boolean {
    const formControlValue =
      this.getMonumentInstallationDateRangeFormControl().value || {};

    return DateHelperUtil.isToDateNotLessThanFromDate(
      formControlValue.from,
      formControlValue.to,
    );
  }

  isMonumentInstallationDateRangeEmpty(): boolean {
    const formControlValue =
      this.getMonumentInstallationDateRangeFormControl().value || {};

    return _.isEmpty(formControlValue.from) && _.isEmpty(formControlValue.to);
  }

  updateMonumentInstallationDateRangeFormControl(
    dateRange: NgbDateRange,
  ): void {
    this.getMonumentInstallationDateRangeFormControl().setValue(dateRange);
  }

  getMonumentInstallationDateRangeFormControl(): AbstractControl {
    return this.formGroup.get(this.controlName.MonumentInstallationDateRange);
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
